import React, { useEffect, useRef, useState } from 'react';
import './iasearch.css';
import CardViewAd from '../cardViews/CardViewAd';
import ViewAdDetails from '../../views/ViewAdDetails';
import PromptsContainer from '../promptsContainer/promptsContainer';
import getStoredValue from '../../services/UseLocalStorage';
import { NAME_HEADER_AUTH } from '../../utils/Constants';

// Simple no-op handlers to satisfy CardViewAd props
const noop = () => {};

export default function IASearchView({ initialQuery, fadeIn = false }) {
      // Track if the last page fetch returned zero items
      const [lastPageWasEmpty, setLastPageWasEmpty] = useState(false);
    // Track the current limit used for paging
    const currentLimitRef = useRef(10);
  // Chat UI state
  const [messages, setMessages] = useState([
    { id: 1, role: 'ai', text: 'Hola, soy PulpoIA. ¿Qué quieres que investigue o genere hoy?' }
  ]);
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);

  // Results + pagination state
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [urls, setUrls] = useState([]);
  const [urlIndex, setUrlIndex] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Scroll container ref for results grid (right panel)
  const resultsRef = useRef(null);

  // Control whether the next fetch should replace or append items
  const replaceOnNextFetchRef = useRef(true);

  // Details modal state (used now as side panel toggle)
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Track current saved user prompt id and whether we've already updated its succeded flag
  const userPromptIdRef = useRef(null);
  const succededUpdatedRef = useRef(false);

  // NEW: Track if we've exhausted all URLs
  const urlsExhaustedRef = useRef(false);

  // Auto-submit initial query on mount
  useEffect(() => {
    if (initialQuery && initialQuery.trim()) {
      setInput(initialQuery);
      // Add user message immediately
      const userMsg = { id: Date.now(), role: 'user', text: initialQuery.trim() };
      setMessages((m) => [...m, userMsg]);
      
      // Trigger submit after component mounts
      setTimeout(() => {
        handleAutoSubmit(initialQuery.trim());
      }, 100);
    }
  }, [initialQuery]);

  // Handle auto-submit for initial query (separate from form submit)
  const handleAutoSubmit = async (queryText) => {
    await saveUserPrompt(queryText);

    try {
      setLoading(true);
      setError(null);

      const [mainResponse] = await Promise.allSettled([
        fetch('https://juan1ondonolinks.com/userask/userask', {
        //fetch('http://127.0.0.1:8000/userask', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userprompt: queryText })
        }),
        triggerBackgroundResearch(queryText)
      ]);

      if (mainResponse.status === 'fulfilled') {
        const contentType = mainResponse.value.headers.get('content-type') || '';
        const data = contentType.includes('application/json') 
          ? await mainResponse.value.json() 
          : await mainResponse.value.text();
        const payload = typeof data === 'string' ? JSON.parse(data) : data;

        if (payload && Array.isArray(payload.urls) && payload.urls.length > 0) {
          setUrls(payload.urls);
          setUrlIndex(0);
          setPage(1);
          setTotalPages(1);
          setItems([]);
          replaceOnNextFetchRef.current = true;
          urlsExhaustedRef.current = false;

          const aiMsg = { id: Date.now() + 1, role: 'ai', text: `Perfecto. Buscaré en ${payload.urls.length} fuentes...` };
          setMessages((m) => [...m, aiMsg]);
        } else {
          setUrls([]);
          setItems([]);
          setError('La respuesta no contiene URLs.');
        }
      } else {
        setUrls([]);
        setItems([]);
        setError('No se pudo contactar con PulpoIA.');
      }
    } catch (err) {
      console.error('PulpoIA request failed:', err);
      setUrls([]);
      setItems([]);
      setError('No se pudo contactar con PulpoIA.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const hasMoreData = () => {
    if (urls.length === 0) return false;
    if (page < totalPages) return true;
    if (urlIndex < urls.length - 1) return true;
    return false;
  };

  // NEW: Check if all URLs have been exhausted
  const hasMoreUrls = () => {
    return urls.length > 0 && urlIndex < urls.length - 1;
  };

  // Save a user prompt to local backend, capturing its id
  const saveUserPrompt = async (prompt) => {
    try {
      const res = await fetch('https://pulpoia-ops.com/backend/ads/userprompts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userprompt: prompt })
      });
      if (!res.ok) throw new Error('POST userprompt failed');
      const data = await res.json();
      // Expected shape: { message, id, prompt, succeded: false }
      if (data && typeof data.id !== 'undefined') {
        userPromptIdRef.current = data.id;
        succededUpdatedRef.current = false; // reset for new prompt
      }
    } catch (err) {
      console.error('Error saving user prompt:', err);
      userPromptIdRef.current = null;
    }
  };

  // Update succeded flag once we confirm there are results (totalPages > 0)
  const updateUserPromptSucceded = async (id) => {
    if (id == null) return;
    try {
      await fetch(`https://pulpoia-ops.com/backend/ads/userprompts/${id}/succeded`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ succeded: true })
      });
    } catch (err) {
      console.error('Error updating succeded flag:', err);
    }
  };

  // NEW: Background research function
  const triggerBackgroundResearch = async (prompt) => {
    try {
      const auth = getStoredValue(NAME_HEADER_AUTH);
      if (!auth) {
        console.warn('No auth token found for background research');
        return;
      }

      //await fetch('https://juan1ondonolinks.com/userask/research', {
        //await fetch('http://localhost:8000/research', {
        //await fetch('http://localhost:8080/research-runner', {
        await fetch('https://pulpodevia.com/research-runner', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            userprompt: prompt,
            token: auth 
          })
      });
      // Fire and forget - no need to handle response
    } catch (err) {
      console.error('Background research failed (non-critical):', err);
    }
  };

  // NEW: Research fallback function
  const tryResearchFallback = async () => {
    try {
      // Get the last user message as the prompt
      const lastUserMessage = messages.filter(m => m.role === 'user').pop();
      if (!lastUserMessage) {
        setError('No hay resultados.');
        return;
      }

      const prompt = encodeURIComponent(lastUserMessage.text);
      const res = await fetch(`https://pulpoia-ops.com/backend/ads/researchuser?UserPrompt=${prompt}&page=1&limit=20`);
      
      if (!res.ok) throw new Error(`Research API failed: ${res.status}`);
      
      const data = await res.json();
      const researchAds = Array.isArray(data?.data) ? data.data : [];
      
      if (researchAds.length > 0) {
        setItems(researchAds);
        setPage(1);
        setTotalPages(1);
        
        // Add encouraging AI message
        const aiMsg = { 
          id: Date.now(), 
          role: 'ai', 
          text: `Encontré ${researchAds.length} anuncios adicionales de Facebook Ads Library para tu búsqueda.` 
        };
        setMessages((m) => [...m, aiMsg]);
        
        // Mark the prompt as successful since we found results
        if (!succededUpdatedRef.current && userPromptIdRef.current != null) {
          succededUpdatedRef.current = true;
          updateUserPromptSucceded(userPromptIdRef.current);
        }
      } else {
        setError('No hay resultados disponibles en este momento.');
      }
    } catch (err) {
      console.error('Research fallback failed:', err);
      setError('No hay resultados.');
    }
  };

  // Enhanced fetchAdsPage: auto-paginate through empty pages, increase limit, show UI feedback, avoid infinite loops
  const fetchAdsPage = async (
    url,
    pageNum = 1,
    append = false,
    emptyPageCount = 0,
    currentLimit = 10
  ) => {
    if (!url) return;
    setLoading(true);
    setError(null);
    // Always update the ref so UI can use it
    currentLimitRef.current = currentLimit;
    try {
      const urlObj = new URL(url);
      urlObj.searchParams.set('page', pageNum);
      urlObj.searchParams.set('limit', currentLimit);
      const finalUrl = urlObj.toString();

      // UI feedback for skipping empty pages
      if (emptyPageCount > 0) {
        setError('Buscando anuncios activos…');
      }

      console.log('OJO Fetching ads from URL:', finalUrl);

      const res = await fetch(finalUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const contentType = res.headers.get('content-type') || '';
      const data = contentType.includes('application/json') ? await res.json() : await res.text();
      const payload = typeof data === 'string' ? JSON.parse(data) : data;

      const ads = Array.isArray(payload?.data) ? payload.data : [];
      const info = payload?.info || {};
      setPage(info.currentPage || pageNum);
      setTotalPages(info.totalPages || 1);
      if (ads.length > 0) {
        setItems((prev) => (append ? [...prev, ...ads] : ads));
        setError(null);
        setLastPageWasEmpty(false);

        // Auto-fetch next page if results are less than limit and more pages exist
        if (ads.length < currentLimit && pageNum < info.totalPages) {
          await fetchAdsPage(url, pageNum + 1, true, emptyPageCount, currentLimit);
          return;
        }
      } else {
        // If we got zero ads, mark lastPageWasEmpty true
        setLastPageWasEmpty(true);
      }

      // Mark prompt as succeded if we have at least one page (only once)
      if (!succededUpdatedRef.current && userPromptIdRef.current != null) {
        const tp = Number(info.totalPages);
        if (!Number.isNaN(tp) && tp > 0) {
          succededUpdatedRef.current = true;
          updateUserPromptSucceded(userPromptIdRef.current);
        }
      }

      // Enhanced: If data is empty but totalItems > 0, keep paginating
      if (ads.length === 0 && info.totalItems > 0 && pageNum < info.totalPages) {
        // If too many empty pages, stop and show error
        if (emptyPageCount >= 10) {
          setError('No se encontraron anuncios activos, prueba cambiando los filtros.');
          setLoading(false);
          return;
        }
        // If 3+ consecutive empty pages, increase limit to 10
        const nextLimit = emptyPageCount >= 3 ? 10 : currentLimit;
        // Recursively fetch next page
        await fetchAdsPage(url, pageNum + 1, append, emptyPageCount + 1, nextLimit);
        return;
      }

      // Smart URL progression - try next URL before research fallback
      if (ads.length === 0 && !hasMoreData()) {
        if (hasMoreUrls() && !urlsExhaustedRef.current) {
          setUrlIndex((prevIndex) => prevIndex + 1);
          setPage(1);
          setTotalPages(1);
          replaceOnNextFetchRef.current = true;
        } else {
          urlsExhaustedRef.current = true;
          // await tryResearchFallback();
        }
      }
    } catch (err) {
      console.error('Failed to fetch ads page:', err);
      setError('Error al cargar resultados.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch first page for current URL when urls or urlIndex changes
  useEffect(() => {
    if (urls.length > 0 && urlIndex < urls.length) {
      const append = !replaceOnNextFetchRef.current ? true : false;
      fetchAdsPage(urls[urlIndex], 1, append);
      // After the first fetch following a new urls set, subsequent URL changes should append
      // UNLESS we're moving due to empty results (replaceOnNextFetchRef handles this)
      if (replaceOnNextFetchRef.current) {
        replaceOnNextFetchRef.current = false;
      }
    }
  }, [urls, urlIndex]);

  // Infinite scroll bound to the right panel grid, which has its own scrollbar
  useEffect(() => {
    const el = resultsRef.current;
    if (!el) return;

    const handleScroll = () => {
      if (loading || urls.length === 0 || !hasMoreData()) return;
      const { scrollTop, clientHeight, scrollHeight } = el;
      if (scrollTop + clientHeight >= scrollHeight - 120) {
        if (page < totalPages) {
          fetchAdsPage(urls[urlIndex], page + 1, true);
        } else if (urlIndex + 1 < urls.length) {
          // Move to next URL and append its first page
          replaceOnNextFetchRef.current = false;
          setUrlIndex((i) => i + 1);
          setPage(1);
          setTotalPages(1);
        }
      }
    };

    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [loading, urls, urlIndex, page, totalPages]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed) return; // Avoid sending empty prompts

    // Echo user message locally
    const userMsg = { id: Date.now(), role: 'user', text: trimmed };
    setMessages((m) => [...m, userMsg]);
    setInput('');

    // Save prompt locally first so we have its id for later updating
    await saveUserPrompt(trimmed);

    // PARALLEL REQUESTS - both fire simultaneously
    try {
      setLoading(true);
      setError(null);

      const [mainResponse] = await Promise.allSettled([
        // Original request
        fetch('https://juan1ondonolinks.com/userask/userask', {
        //fetch('http://localhost:8000/userask', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userprompt: trimmed })
        }),
        // NEW: Background scraping request
        triggerBackgroundResearch(trimmed)
      ]);

      if (mainResponse.status === 'fulfilled') {
        const contentType = mainResponse.value.headers.get('content-type') || '';
        const data = contentType.includes('application/json') 
          ? await mainResponse.value.json() 
          : await mainResponse.value.text();
        const payload = typeof data === 'string' ? JSON.parse(data) : data;

        // Expect payload.urls: string[]
        if (payload && Array.isArray(payload.urls) && payload.urls.length > 0) {
          setUrls(payload.urls);
          setUrlIndex(0);
          setPage(1);
          setTotalPages(1);
          setItems([]);
          replaceOnNextFetchRef.current = true; // First fetch should replace
          
          // Reset URL exhaustion tracking
          urlsExhaustedRef.current = false;

          // Add a short AI confirmation message
          const aiMsg = { id: Date.now() + 1, role: 'ai', text: `Perfecto. Buscaré en ${payload.urls.length} fuentes...` };
          setMessages((m) => [...m, aiMsg]);
        } else {
          setUrls([]);
          setItems([]);
          setError('La respuesta no contiene URLs.');
        }
      } else {
        setUrls([]);
        setItems([]);
        setError('No se pudo contactar con PulpoIA.');
      }
    } catch (err) {
      console.error('PulpoIA request failed:', err);
      setUrls([]);
      setItems([]);
      setError('No se pudo contactar con PulpoIA.');
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      onSubmit(e);
    }
  };

  // Open/close details modal
  const openDetails = (item) => {
    setSelectedItem(item);
    setDetailsOpen(true);
  };
  const closeDetails = () => {
    setDetailsOpen(false);
    setSelectedItem(null);
  };

  // Close on ESC for side panel
  useEffect(() => {
    if (!detailsOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') closeDetails(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [detailsOpen]);

  return (
    // Match CSS: use ia-search-layout for grid with two columns
    <div className={`ia-search-layout ${fadeIn ? 'fade-in' : ''}`}>
      {/* Left: Chat */}
      <section className="ia-chat-panel">
        <header className="ia-chat-header">
          <h2>PulpoIA</h2>
          <p className="ia-subtitle">Describe lo que quieres investigar y PulpoIA buscará anuncios.</p>
        </header>
        <div className="ia-chat-messages" id="ia-chat-messages">
          {messages.map((m) => (
            <div key={m.id} className={`ia-chat-bubble ${m.role}`}>
              {m.text}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
        {/* Helpful prompt examples above the text area */}
        <div className="ia-prompts-wrapper" style={{ padding: '8px 12px' }}>
          <PromptsContainer group="landing_prompts" onSelect={(p) => { setInput(p); const ta = document.querySelector('.ia-chat-input textarea'); if (ta) ta.focus(); }} />
        </div>
        <form className="ia-chat-input" onSubmit={onSubmit}>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Escribe tu prompt... (Ctrl+Enter para enviar)"
            rows={4}
          />
          <button type="submit" className="ia-send">Enviar</button>
        </form>
      </section>

      {/* Right: Results with side details */}
      <section className="ia-results-panel">
        <div className="ia-results-header">
          <h2>Resultados</h2>
          <div className="ia-results-meta">
            {loading ? (
              <span style={{display:'flex',alignItems:'center',gap:8}}>
                <span>Cargando…</span>
                <span className="ia-progress" style={{position:'relative',display:'inline-block',width:160,height:6,background:'#1f2937',borderRadius:6,overflow:'hidden'}}>
                  <span style={{position:'absolute',left:0,top:0,bottom:0,width:'40%',background:'#3b82f6',borderRadius:6,animation:'ia-bar 1.2s infinite ease-in-out'}} />
                </span>
              </span>
            ) : (
              <span>{Array.isArray(items) ? items.length : 0} items</span>
            )}
          </div>
        </div>

        {error && (
          <div style={{color:'#ef4444', padding: '8px 12px'}}>{error}</div>
        )}

        <div className={`ia-results-body ${detailsOpen && selectedItem ? 'with-details' : ''}`}>
          <div className="ia-grid" ref={resultsRef}>
            {/* If no items, show message and button if more pages exist */}
            {(!Array.isArray(items) || items.length === 0) ? (
              <>
                <div style={{gridColumn: '1 / -1', color: '#9ca3af', padding: '12px'}}>
                  {error ? 'Intenta de nuevo o modifica tu prompt.' : 'No hay resultados todavía. Envía un prompt para empezar.'}
                </div>
                {page < totalPages && (
                  <div style={{gridColumn: '1 / -1', textAlign: 'center', margin: '16px 0'}}>
                    <button
                      style={{
                        background: '#3b82f6', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: 8,
                        fontWeight: 600, fontSize: 16, cursor: 'pointer', boxShadow: '0 2px 8px #0002'
                      }}
                      onClick={() => fetchAdsPage(urls[urlIndex], page + 1, true, 0, currentLimitRef.current)}
                    >
                      Ver más anuncios
                    </button>
                  </div>
                )}
              </>
            ) : (
              <>
                {items.map((item, idx) => (
                  <div
                    key={item.LibraryID || `${item.id || 'ad'}-${idx}`}
                    className="ia-grid-item ia-grid-item--clickable"
                    role="button"
                    tabIndex={0}
                    onClick={() => openDetails(item)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openDetails(item); }}
                  >
                    <CardViewAd
                      index={idx}
                      item={item}
                      launchCategoryDialog={noop}
                      gridShowAdDetail={noop}
                    />
                  </div>
                ))}
                {/* Show 'Ver más anuncios' button if results < limit and more pages exist, or last page was empty and more pages exist */}
                {( (items.length < currentLimitRef.current && page < totalPages) || (lastPageWasEmpty && page < totalPages) ) && (
                  <div style={{gridColumn: '1 / -1', textAlign: 'center', margin: '16px 0'}}>
                    <button
                      style={{
                        background: '#3b82f6', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: 8,
                        fontWeight: 600, fontSize: 16, cursor: 'pointer', boxShadow: '0 2px 8px #0002'
                      }}
                      onClick={() => fetchAdsPage(urls[urlIndex], page + 1, true, 0, currentLimitRef.current)}
                    >
                      Ver más anuncios
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {detailsOpen && selectedItem && (
            <aside className="ia-details-panel" aria-label="Detalles del anuncio">
              <button className="ia-details-close" onClick={closeDetails} aria-label="Cerrar">✕</button>
              <ViewAdDetails item={selectedItem} launchCategoryDialog={noop} isVault={false} />
            </aside>
          )}
        </div>
      </section>
    </div>
  );
}
